import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, Input, ChangeDetectorRef } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { EngineService } from 'src/app/engine/engine.service';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { BackgroundModel } from 'src/app/engine/models/background.model';
import { CameraModel } from 'src/app/engine/models/camera.model';
import { SetCamera } from 'src/app/engine/state/camera/engine.camera.actions';
import { QuizQuestion, QuizQuestionUnion, SelectAnatomyQuestion } from 'src/app/quiz/models/quiz-question.model';
import { QuizActions } from 'src/app/quiz/state/quiz.actions';
import { QuizLaunchMode, QuizState } from 'src/app/quiz/state/quiz.state';
import { SelectChapterById, SetChapterBackground } from 'src/app/story/state/chapters/story.chapters.actions';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { ViewService } from '../../view.service';
import { QuizQuestionType } from 'src/app/quiz/models/quiz-question-type.enum';
import { ToolEnum } from 'src/app/engine/models/tool.enum';
import { EngineState } from 'src/app/engine/state/engine.state';
import { ClickNode } from 'src/app/engine/state/engine.actions';
import { get, isEqual, isNil } from 'lodash';
import { EngineNodesState } from 'src/app/engine/state/nodes/engine.nodes.state';
import { NodeActions } from 'src/app/engine/state/nodes/engine.nodes.actions';
import { defaultNextButtonNext, hasValidAnswers } from 'src/app/quiz/config/quiz';
import { QuizSettingsModel } from 'src/app/quiz/models/quiz-settings.model';
import { QuizLifecycleState } from 'src/app/quiz/models/quiz-lifecycle.enum';
import { QuizService } from './view-quiz.service';
import { QuestionSubmission } from 'src/app/quiz/models/question-submission.model';
import { PendoService } from 'src/app/shared/pendo.service';
import { StoryState } from 'src/app/story/state/story.state';


@Component({
  selector: 'app-view-quiz',
  templateUrl: './view-quiz.component.html',
  styleUrls: ['./view-quiz.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewQuizComponent implements OnInit, OnDestroy {
  
  defaultNextButtonNext = defaultNextButtonNext;

  @Select(QuizState.getQuizReviewEnabled)
  quizReviewEnabled$: Observable<boolean>;

  @Select(QuizState.getActiveQuestion)
  question$: Observable<QuizQuestion>;

  @Select(QuizState.getAllQuestions)
  questions$: Observable<QuizQuestion>;

  @Select(QuizState.getActiveQuestionIndex)
  questionIndex$: Observable<number>;

  @Select(QuizState.getQuestionTotal)
  questionTotal$: Observable<number>;

  @Select(QuizState.getCorrectAnswerTotal)
  correctAnswerTotal$: Observable<number>;

  @Select(QuizState.hasQuestionRemaining)
  hasQuestionsRemaining$: Observable<boolean>;

  @Select(QuizState.getQuizSettings)
  quizSettings$: Observable<QuizSettingsModel>;

  @Select(QuizState.getLifecycle)
  lifecycle$: Observable<QuizLifecycleState>;
  
  @Select(QuizState.getQuizModeLocked)
  quizLocked$: Observable<boolean>;

  @Select(QuizState.getActiveSubmission)
  submission$: Observable<QuestionSubmission>;

  @Input()
  showHeader = true;

  Lifecycle = QuizLifecycleState;
  form: FormGroup;
  quizQuestionType = QuizQuestionType;


  private sceneConfigStash: Record<string, {background?: BackgroundModel, camera?: CameraModel}> = {};
  private ngUnsubscribe = new Subject();
  
  constructor(
    private store: Store,
    private actions: Actions,
    private formBuilder: FormBuilder,
    private viewService: ViewService,
    private engineService: EngineService,
    private quizService: QuizService,
    private pendoService: PendoService
  ) {}

  

  ngOnInit() {
    // Sometimes we re-enter the quiz in the middle (e.g after using whiteboard)
    // For that we use the activeQuestionId
    // Other times, it's a fresh start. This ensures you start in the right place. 
    if (!this.store.selectSnapshot(QuizState.getActiveQuestionId)) {
      const questionId = this.store.selectSnapshot(QuizState).questions[0].id;
      this.store.dispatch(new QuizActions.SetActiveQuestion(questionId));
    }
    this.store.dispatch(new QuizActions.SetQuizLifecycle(QuizLifecycleState.Question));
    
    // set active question id if undefined.
    this.form = this.formBuilder.group({
      'question': new FormControl('', this.getQuizValidation())
    });

    this.initQuestionObserver();
    this.initAnswerObserver();
    this.initEngineInteractionObserver();

    this.engineService.fireEvent(EventsEnum.QuizEntered, true);
  }

  ngOnDestroy() {
    // restore chapter backgrounds
    Object.entries(this.sceneConfigStash).forEach(([chapterId, stash]) => {
      this.store.dispatch(new SetChapterBackground(chapterId, stash.background));
    });

    this.engineService.fireEvent(EventsEnum.QuizExited, true);
    this.viewService.selectTool(ToolEnum.Select);

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  get answer(): string[] {
    const val: string | string[] = this.form.get('question').value;
    return Array.isArray(val) ? val : [val];
  }

  initQuestionObserver() {
    this.question$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((question) => question !== undefined)
      )
      .subscribe((question) => {
        // navigate to the correct chapter if needed
        const currentChapterId = this.store.selectSnapshot(
          StoryChaptersState.getSelectedChapterId
        );
        const shouldChangeChapter = question.chapterId !== currentChapterId;
        if (shouldChangeChapter) {
          this.store.dispatch(new SelectChapterById(question.chapterId));
        }

        this.form.reset();
        this.setSceneConfig(question);

        // reset active submission
        this.store.dispatch(new QuizActions.SetActiveSubmission(undefined));

        // add default value for text only question
        if (question.type === QuizQuestionType.Instructions) {
          this.form.get('question').setValue('-');
        }
          
        // fire API event
        this.engineService.fireEvent(EventsEnum.QuizQuestionLoaded, question);
      });
  }

  initAnswerObserver() {
    this.form.get('question').valueChanges
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((answer) => {
        const question = this.store.selectSnapshot(QuizState.getActiveQuestion);
        const submission = this.store.selectSnapshot(QuizState.getActiveSubmission);

        if (question === undefined) {
          return;
        }

        if (this.store.selectSnapshot(QuizState.getAllSubmissions).some(q => q.questionId === question.id)) {
          return;
        }

        if (!submission || !isEqual(submission.answer, answer)) {
          this.store.dispatch(new QuizActions.SetActiveSubmission({
            questionId: question.id,
            answer: answer
          }));
        }

        if (!isNil(answer)) {
          this.engineService.fireEvent(EventsEnum.QuizAnswerSelected, {
            questionId: question.id,
            answerId: answer
          });
        }
      });
  }

  initEngineInteractionObserver() {
    // set quiz tools + selection mode
    this.viewService.selectTool(ToolEnum.Quiz);
    this.store.dispatch(new QuizActions.SetQuizPickMode('single'));

    // handle node interaction
    const nodeClicked$ = this.actions
      .pipe(
        ofActionDispatched(ClickNode),
        filter((nodeAction: ClickNode) => {
          const { node, event } = nodeAction;
          return !isNil(node)
            && this.store.selectSnapshot(EngineState.getActiveTool) === ToolEnum.Quiz
            && !event.srcEvent.shiftKey;
        })
      );

    // set node click logic
    nodeClicked$
      .pipe(
        filter(() => this.store.selectSnapshot(QuizState.getPickMode) !== 'none'),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((clickNode) => {
        const pickMode = this.store.selectSnapshot(QuizState.getPickMode);
        if (pickMode === 'single') {
          this.viewService.clearSelectedNodes();
        }

        // toggle picked
        const objectId: string = get(clickNode, 'hit.objectId');
        if (objectId) {
          const checkedNodesIds = this.store.selectSnapshot(EngineNodesState.getCheckedNodeIds);
          const isChecked = checkedNodesIds.includes(objectId);

          this.store.dispatch(
            new NodeActions.UpdateNodesChecked(!isChecked, [objectId])
          );
        }
      });

    const questionChange$ = this.question$
      .pipe(
        distinctUntilChanged((last, current) => get(last, 'id') === get(current, 'id')),
        filter((question) => !isNil(question))
      );

    // clear selected nodes on question change
    questionChange$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((question) => {
        const { type, data } = question;

        // clear selections
        this.viewService.clearSelectedNodes();

        // set pick mode: single unless requiring multiple selctions
        const isMultiPick = type === QuizQuestionType.SelectAnatomy
          ? data.correctAnswer.length > 1
          : false;

        this.store.dispatch(new QuizActions.SetQuizPickMode(isMultiPick ? 'multi' : 'single'));
      });
  }

  handleSubmit() {
    const question = this.store.selectSnapshot(QuizState.getActiveQuestion);
    if (this.form.valid || question.type === QuizQuestionType.Instructions) {
      this.quizService.submitQuestion(question.id, this.answer);
    }
  }
  
  handleViewSummaryClick() {
    this.viewSummary();
  }

  handleExitClick() {
    this.quizService.exitQuiz();
  }


  nextQuestion() {
    this.quizService.nextQuestion();
  }

  viewSummary() {
    this.store.dispatch(new QuizActions.SetQuizLifecycle(QuizLifecycleState.Review));
    this.engineService.fireEvent(EventsEnum.QuizCompleted, true);
  }

  private setSceneConfig(question: QuizQuestionUnion) {
    const { camera, background } = question?.sceneConfig || {};
    const chapter = this.store.selectSnapshot(StoryChaptersState.getChapter(question.chapterId));

    if (camera) {
      this.setStash(question.chapterId, {camera: chapter.camera.location});
      setTimeout(() => {
        this.store.dispatch(new SetCamera(camera.location, true, 1));
      });
    }

    if (background) {
      this.setStash(question.chapterId, {background: chapter.background});
      this.store.dispatch(new SetChapterBackground(question.chapterId, background));
    }
  }

  private setStash(chapterId: string, stash: {background?: BackgroundModel, camera?: CameraModel}) {
    const oldStash = this.sceneConfigStash[chapterId] || {};
    this.sceneConfigStash[chapterId] = {...oldStash, ...stash};
  }

  // validate selected values
  private getQuizValidation(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const formValue: string | string[] = control.value;
      const values: string[] = Array.isArray(formValue) ? formValue : [formValue];

      // TODO: Should be in edit form validation, not enabling empty answers
      // validate available answers: check for empty correct answers
      const question = this.store.selectSnapshot(QuizState.getActiveQuestion);
      const hasValidAnswerChoices = hasValidAnswers(question);

      const invalidValues = values
        .some((value) => isNil(value) || value === '');

      if (hasValidAnswerChoices && invalidValues) {
        return { invalidValues };
      } else {
        return null;
      }
    };
  }
}
